import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

// constants
import { ACCESS_TYPE } from "@/store/actions/universityDetailsActions";
import { USER_TYPE, updateUserInReportAndStore } from "@/store/actions/reportsActions";
import { textValues } from "./constants";

// components
import TableCell from "./cell";
import ExpandableOptions from "./expandable";
import NoDataPreview from "@/components/shared/noDataPreview";

// mui
import {
    Box,
    Grid,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell as MuiTableCell,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
    headingList: {
        marginTop: theme.spacing(3),
        alignItems: "center",
        position: "relative",
    },
    reportList: {
        marginTop: theme.spacing(0.5),
        borderRadius: "5px",
        border: "1px solid #D8D7D7",
        "& > *": {
            padding: theme.spacing(2, 1),
        },
        "& > * + *": {
            borderTop: "1px solid #ADADAD",
        },
    },
    iconWrap: {
        textAlign: "right",
        fontSize: "20px",
    },
    table: {
        minWidth: 650,
    },
    muiTableCell: {
        maxWidth: "200px",
    },
    noDataText: {
        textAlign: "center",
    },
}));

export const UserTypeCellBody = ({ value, registrantCode, expandOnTop }) => {
    const dispatch = useDispatch();
    const universityDetails = useSelector((state) => state.universityDetails.data);

    const [loading, setLoading] = React.useState(false);

    const handleUpdateRegistrant = (userType, cb) => {
        setLoading(true);
        dispatch(
            updateUserInReportAndStore(
                {
                    access: ACCESS_TYPE.counsellor,
                    user_type: userType,
                    unique_code: registrantCode,
                    fair_id: universityDetails.fairID,
                },
                () => {
                    setLoading(false);
                    cb && cb();
                }
            )
        );
    };

    return (
        <ExpandableOptions
            loading={loading}
            expandOnTop={expandOnTop}
            label={{ text: value ? value : "-", endIcon: true }}
            onClick={(e, option, cb) => {
                e.stopPropagation();
                e.preventDefault();
                handleUpdateRegistrant(option.text, cb);
            }}
            expandableOptions={[
                { text: USER_TYPE.student },
                { text: USER_TYPE.internalTeam },
                { text: USER_TYPE.universityRepresentative },
                { text: USER_TYPE.attendedOnlineForOffline },
            ].filter(({ text }) => text !== value)}
        />
    );
};
UserTypeCellBody.propTypes = {
    value: PropTypes.string,
    registrantCode: PropTypes.string.isRequired,
    expandOnTop: PropTypes.bool,
};

const TableCellBody = ({ data, cellData, expandOnTop }) => {
    if (cellData.Component) {
        return <cellData.Component {...data} expandOnTop={expandOnTop} value={data[cellData.valueKey]} />;
    }

    return (
        <TableCell
            color={cellData.color}
            size={cellData.size}
            weight={cellData.weight}
            ellipsis={cellData.ellipsis}
            tooltip={cellData.tooltip}
            tooltipValue={data[cellData.tooltipValueKey || cellData.valueKey]}
            value={data[cellData.valueKey]}
        />
    );
};
TableCellBody.propTypes = {
    data: PropTypes.object.isRequired,
    cellData: PropTypes.object.isRequired,
    expandOnTop: PropTypes.bool,
};

export const DesktopTable = ({ loading, data, colsData }) => {
    const classes = useStyles();

    const renderLoaderRows = () => {
        return Array.from({ length: 3 }, (_, i) => (
            <Grid container key={i} spacing={1}>
                {colsData.map((item) => (
                    <Grid item key={item.label} xs={item.col}>
                        <Skeleton width={"100%"} />
                    </Grid>
                ))}
            </Grid>
        ));
    };

    const renderDataRows = () => {
        if (data.length === 0) {
            return <NoDataPreview title={textValues.noDataText} />;
        }

        return data.map((report, index) => (
            <Grid container key={report.registrantCode} spacing={1}>
                {colsData.map((item) => (
                    <Grid item key={item.label} xs={item.col}>
                        <TableCellBody cellData={item} data={report} expandOnTop={index >= data.length - 3} />
                    </Grid>
                ))}
            </Grid>
        ));
    };

    return (
        <>
            <Grid container spacing={1} className={classes.headingList}>
                {colsData.map((item) => (
                    <Grid item key={item.label} xs={item.col}>
                        <TableCell
                            color={"default"}
                            size={"default"}
                            weight={"medium"}
                            ellipsis={false}
                            tooltip={false}
                            tooltipValue={item.label}
                            value={item.label}
                        />
                    </Grid>
                ))}
            </Grid>
            <Box className={classes.reportList}>{loading ? renderLoaderRows() : renderDataRows()}</Box>
        </>
    );
};
DesktopTable.propTypes = {
    loading: PropTypes.bool,
    data: PropTypes.array.isRequired,
    colsData: PropTypes.array.isRequired,
};

export const MobileTable = ({ loading, data, colsData }) => {
    const classes = useStyles();

    const renderLoaderRows = () => {
        return Array.from({ length: 3 }, (_, i) => (
            <TableRow key={i}>
                {colsData.map((item) => (
                    <MuiTableCell key={item.label} className={classes.muiTableCell}>
                        <Skeleton width={"100%"} />
                    </MuiTableCell>
                ))}
            </TableRow>
        ));
    };

    const renderDataRows = () => {
        if (data.length === 0) {
            return (
                <TableRow>
                    <MuiTableCell className={classes.muiTableCell} colSpan={colsData.length}>
                        <NoDataPreview title={textValues.noDataText} />
                    </MuiTableCell>
                </TableRow>
            );
        }
        return data.map((report) => (
            <TableRow key={report.registrantCode}>
                {colsData.map((item) => (
                    <MuiTableCell key={item.label} className={classes.muiTableCell}>
                        <TableCellBody cellData={item} data={report} />
                    </MuiTableCell>
                ))}
            </TableRow>
        ));
    };

    return (
        <TableContainer component={Paper}>
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>
                        {colsData.map((item) => (
                            <MuiTableCell key={item.label} className={classes.muiTableCell}>
                                {item.label}
                            </MuiTableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>{loading ? renderLoaderRows() : renderDataRows()}</TableBody>
            </Table>
        </TableContainer>
    );
};
MobileTable.propTypes = {
    loading: PropTypes.bool,
    data: PropTypes.array.isRequired,
    colsData: PropTypes.array.isRequired,
};
